import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { SolPresentation } from './present-sol-result';
import { SolSourceCardLink } from './SolSourceCardLink';
import { SolProjectSourceCardLink } from './SolProjectSourceCardLink';
import { SolReportSourceCardLink } from './SolReportSourceCardLink';
import { SolPhotoSourceCardLink } from './SolPhotoSourceCardLink';
import { SolCompanySourceCard } from './SolCompanySourceCard';
import { SolMyDaySourceCard } from './SolMyDaySourceCard';
import { SolConversationSourceCardLink } from './SolConversationSourceCardLink';
import { SolMemorySourceCard } from './SolMemorySourceCard';
import { SolWebSourceCardLink } from './SolWebSourceCardLink';
import {
  isCaptureSourceCard,
  isCompanySourceCard,
  isConversationSourceCard,
  isMyDaySourceCard,
  isPhotoSourceCard,
  isProjectSourceCard,
  isReportSourceCard,
  isWebSourceCard,
  type SolSourceCard,
  isMemorySourceCard,
} from '../../data/sol/sol-contract';
import { captureDetailPath } from './sol-capture-route';
import { INFERENCE_NOTE, MEMORY_KIND_LABEL, conflictNote, projectLabel } from './sol-wording';
import { formatCaptureTitle } from '../captures/capture-status';

/**
 * ONE SOL RESULT, DRAWN. M9 / S09-04.
 *
 * **THIS COMPONENT MAKES NO CONTRACT DECISIONS.** Every branch it could have
 * needed was already taken by `presentSolResult`, which is exhaustive and
 * compiler-checked. What is left here is layout — which is exactly the point of
 * splitting them: a missing result variant fails the build in a pure function
 * instead of rendering an empty card on a phone.
 *
 * **CLAIMS ARE SHOWN IN SERVER ORDER, WITH THEIR OWN CITATIONS.** Not renumbered,
 * not re-sorted, not merged. A claim's cards were resolved by `{domain, id}`
 * upstream, never by position.
 *
 * **INFERENCE IS MARKED AT THE CLAIM, QUIETLY.** A supported claim beside an
 * inferred one stays supported; stamping the whole answer would demote a fact
 * because of its neighbour.
 *
 * **CONFLICT IS REPORTED, NEVER RESOLVED.** Both readings stay, with their own
 * sources, and Crewlink does not pick.
 */

export interface SolAnswerViewProps {
  readonly presentation: SolPresentation;
  readonly timeZone: string | null;
  /** Manual retry, offered only when `presentation.retryable`. */
  readonly onRetry?: (() => void) | undefined;
  readonly retryDisabled?: boolean | undefined;
  /**
   * The Read Aloud control (Pass B). Rendered only for grounded answers; the
   * caller decides whether one exists at all, so this component never has to
   * know what a provider is.
   */
  readonly readAloud?: ReactNode;
  /**
   * The confirmation card (M11). Rendered only when the server issued a
   * proposal; the caller decides whether one exists at all, so this component
   * never has to know what a ticket is — and **it never performs an action**.
   */
  readonly action?: ReactNode;
}

export function SolAnswerView({
  presentation,
  timeZone,
  onRetry,
  retryDisabled,
  readAloud,
  action,
}: SolAnswerViewProps) {
  const isFailure = presentation.tone === 'failure';

  return (
    <div className={`sol-answer sol-answer--${presentation.tone}`}>
      <p className="sol-answer-label">SOL</p>

      {presentation.body ? (
        <p className={isFailure ? 'sol-answer-body sol-answer-body--failure' : 'sol-answer-body'}>
          {presentation.body}
        </p>
      ) : null}

      {presentation.claims.length > 0 ? (
        <ul className="sol-claims">
          {presentation.claims.map((claim) => (
            <li key={claim.id} className={`sol-claim sol-claim--${claim.grounding}`}>
              <p className="sol-claim-text">{claim.text}</p>

              {claim.grounding === 'inference' ? (
                <p className="sol-claim-note">{INFERENCE_NOTE}</p>
              ) : null}
              {claim.conflictBasis !== null ? (
                <p className="sol-claim-note">{conflictNote(claim.conflictBasis)}</p>
              ) : null}

              {claim.cards.length > 0 ? (
                <ul className="sol-claim-sources">
                  {claim.cards.map((card) => (
                    <li key={`${claim.id}:${card.source.domain}:${card.source.id}`}>
                      {/* **THE CARD DECIDES ITS OWN VIEW, BY ITS DOMAIN** (I-074).
                          Five kinds, one exhaustive switch: a report is a report
                          card, a photo a photo card, and a kind this screen does
                          not know is a compile error, never a mislabelled box. */}
                      {renderSourceCard(card, timeZone)}
                    </li>
                  ))}
                </ul>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}

      {/* CLOSE MATCHES — REAL LINKS, AND VISIBLY NOT CITED EVIDENCE.

          **This was a genuine defect found by Jerry on a real Android phone**,
          and the comment that used to sit here is worth recording rather than
          quietly deleting: it claimed these were "deliberately rendered as the
          same real links" when they were plain `<p>` text with nothing to tap.
          The comment described an intention and the code never carried it out —
          which is how it survived both a self-report and an independent review,
          because a reader who trusts a comment stops looking.

          They are links now, for the reason the old comment gave and could not
          back up: these are real Captures Crewlink is UNSURE about, and the
          honest thing is to let him go and read them himself.

          **They must not LOOK like cited evidence.** A cited source carries the
          blue edge and reads "Open source Capture"; a close match is dashed and
          reads "Open Capture — possible match". Same navigation, different
          promise. */}
      {presentation.candidates.length > 0 ? (
        <ul className="sol-claim-sources sol-candidates">
          {presentation.candidates.map((candidate) => {
            const title = formatCaptureTitle(candidate.captureNumber, candidate.locationDisplay);
            const project = projectLabel(candidate.project.name);
            return (
              <li key={`${candidate.source.domain}:${candidate.source.id}`}>
                <Link
                  className="sol-candidate"
                  // **THE CANDIDATE'S OWN PROJECT, NEVER THE ROUTE'S** — close
                  // matches broaden exactly as cited sources do.
                  to={captureDetailPath(candidate.project.id, candidate.source.id)}
                  // Says what tapping does AND what the record is worth. No id.
                  aria-label={`Open possible match ${title} in ${project}`}
                >
                  <span className="sol-candidate-title">
                    {title} · {project}
                  </span>
                  {candidate.caption ? (
                    <span className="sol-candidate-caption">{candidate.caption}</span>
                  ) : null}
                  <span className="sol-candidate-open" aria-hidden="true">
                    Open Capture — possible match →
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      ) : null}

      {/* The memories a "forget" could mean (M11 / D-040): INFORMATION, not
          controls — he says which in his next question, or opens Memory. */}
      {presentation.memory?.kind === 'candidates' ? (
        <ul className="sol-candidate-projects sol-memory-candidates">
          {presentation.memory.candidates.map((memory) => (
            <li key={memory.id}>
              <span className="sol-memory-candidate-kind">{MEMORY_KIND_LABEL[memory.kind]} · </span>
              {memory.content}
            </li>
          ))}
        </ul>
      ) : null}

      {/* Candidate Projects for a clarification: INFORMATION, not controls. */}
      {presentation.candidateProjectNames.length > 0 ? (
        <ul className="sol-candidate-projects">
          {presentation.candidateProjectNames.map((name, index) => (
            <li key={`${name}:${String(index)}`}>{name}</li>
          ))}
        </ul>
      ) : null}

      {presentation.notes.length > 0 ? (
        <ul className="sol-notes">
          {presentation.notes.map((note) => (
            // **TEXT, NEVER COLOUR ALONE.** Every note reads as a sentence, so
            // nothing depends on noticing a shade on a bright site.
            <li key={note}>{note}</li>
          ))}
        </ul>
      ) : null}

      {/* **THE PROPOSAL SITS BELOW THE ANSWER AND ITS SOURCES**, so the
          Capture it names has already been shown as evidence before anything
          is offered. Nothing has been written at this point. */}
      {action}

      {readAloud}

      {presentation.retryable && onRetry ? (
        <p className="sol-retry-row">
          <button
            type="button"
            className="sol-retry"
            onClick={onRetry}
            disabled={retryDisabled === true}
          >
            Ask again
          </button>
        </p>
      ) : null}
    </div>
  );
}

/**
 * **ONE EXHAUSTIVE SWITCH OVER THE CARD UNION.** The `never` assertion at the
 * end is what turns "a sixth card kind was added" into a type error here rather
 * than a card rendered under the wrong heading.
 */
function renderSourceCard(card: SolSourceCard, timeZone: string | null) {
  // Narrowed by the contract's own guards — the discriminant is nested under
  // `source`, which a `switch` cannot narrow through — and closed by `never`.
  if (isCaptureSourceCard(card)) return <SolSourceCardLink card={card} timeZone={timeZone} />;
  if (isProjectSourceCard(card)) {
    return <SolProjectSourceCardLink card={card} timeZone={timeZone} />;
  }
  if (isReportSourceCard(card)) return <SolReportSourceCardLink card={card} timeZone={timeZone} />;
  if (isPhotoSourceCard(card)) return <SolPhotoSourceCardLink card={card} timeZone={timeZone} />;
  if (isCompanySourceCard(card)) return <SolCompanySourceCard card={card} timeZone={timeZone} />;
  // M11 / D-041: a recalled earlier conversation — provenance, never a record.
  if (isConversationSourceCard(card)) {
    return <SolConversationSourceCardLink card={card} timeZone={timeZone} />;
  }
  // M12 / S12-03: one of his FOLLOW-UPS. It takes no timezone argument — the
  // zone the server bucketed it in travels ON the card, so the card and the My
  // Day screen can never disagree about when something is due.
  if (isMyDaySourceCard(card)) return <SolMyDaySourceCard card={card} />;
  // M11 Checkpoint B / I-078: a PUBLIC WEB page. It takes no timezone — the
  // only date on it is whatever the publisher stated, in their own words.
  if (isWebSourceCard(card)) return <SolWebSourceCardLink card={card} />;
  // Something he asked Sol to REMEMBER. Not a record, not clickable — there is
  // nothing to open, and pretending otherwise would misdescribe the answer.
  if (isMemorySourceCard(card)) return <SolMemorySourceCard card={card} timeZone={timeZone} />;
  const exhaustive: never = card;
  return exhaustive;
}
